import angular from 'angular';
import { IOrganograma } from './models/organograma.model';

export class MeurhOrganogramaService {
    static $inject = ['$http', '$rootScope', '$q', 'nsjRouting'];

    /**
     * Teto de fotos em voo ao mesmo tempo. O organograma chega a ter milhares de nos e,
     * sem esse limite, todos eles viravam requisicao simultanea assim que a tela abria.
     */
    private static readonly MAX_FOTOS_SIMULTANEAS = 5;

    public loading_deferred: angular.IDeferred<unknown>;
    public constructors: any = {};

    /** Foto ja resolvida (ou em voo) por trabalhador — evita refazer a mesma requisicao. */
    private fotosCache: { [chave: string]: angular.IPromise<string | null> } = {};

    /** Object URLs criadas aqui, para revogar quando a tela for destruida. */
    private objectUrls: string[] = [];

    private filaFotos: Array<() => void> = [];
    private fotosEmAndamento: number = 0;

    /** Cancelamento coletivo das fotos — resolvido quando a tela e destruida. */
    private fotosCancelDeferred: angular.IDeferred<unknown>;

    constructor(
        public $http: angular.IHttpService,
        public $rootScope: angular.IScope,
        public $q: angular.IQService,
        public nsjRouting: any,
    ) {
        this.fotosCancelDeferred = this.$q.defer();
    }

    getOrganograma(modo: 'empresa' | 'departamento') {
        this.loading_deferred = this.$q.defer();
        return this.$q((resolve: any, reject: any) => {
            this.$http({
                method: 'GET',
                url: this.nsjRouting.generate('persona_trabalhadores_estrutura_organizacional', angular.extend({}, this.constructors), true),
                timeout: this.loading_deferred.promise
            })
                .then((response: any) => {
                    resolve(response.data);
                })
                .catch((response: any) => {
                    reject(response);
                });

        });
    }

    /**
     * Foto de um trabalhador. `versao` e o `foto_versao` devolvido pela estrutura
     * organizacional (epoch de persona.trabalhadores.lastupdate): quando presente,
     * versiona a URL e permite ao browser cachear sem revalidar.
     *
     * Nunca rejeita — quem nao tem foto resolve `null`, para nao derrubar o carregamento
     * do no inteiro por causa de uma imagem.
     */
    getTrabalhadoresFoto(identifier: string, versao?: number | null): angular.IPromise<string | null> {
        if (!identifier) {
            return this.$q.when(null);
        }

        const chave = identifier + ':' + (versao ? versao : '');

        if (!this.fotosCache[chave]) {
            this.fotosCache[chave] = this.agendarFoto(identifier, versao);
        }

        return this.fotosCache[chave];
    }

    /** Cancela as fotos ainda em voo/enfileiradas e libera as object URLs criadas. */
    limparFotos(): void {
        this.filaFotos = [];
        this.fotosEmAndamento = 0;
        this.fotosCancelDeferred.resolve(null);
        this.fotosCancelDeferred = this.$q.defer();

        this.objectUrls.forEach((url: string) => URL.revokeObjectURL(url));
        this.objectUrls = [];
        this.fotosCache = {};
    }

    private agendarFoto(identifier: string, versao?: number | null): angular.IPromise<string | null> {
        const deferred = this.$q.defer<string | null>();

        this.filaFotos.push(() => {
            this.requisitarFoto(identifier, versao)
                .then((url: string | null) => deferred.resolve(url))
                .catch(() => deferred.resolve(null))
                .finally(() => {
                    this.fotosEmAndamento--;
                    this.processarFilaFotos();
                });
        });

        this.processarFilaFotos();

        return deferred.promise;
    }

    private processarFilaFotos(): void {
        while (this.fotosEmAndamento < MeurhOrganogramaService.MAX_FOTOS_SIMULTANEAS && this.filaFotos.length > 0) {
            const tarefa = this.filaFotos.shift();
            this.fotosEmAndamento++;
            tarefa();
        }
    }

    private requisitarFoto(identifier: string, versao?: number | null): angular.IPromise<string | null> {
        const params: any = { 'id': identifier };

        if (versao) {
            params.v = versao;
        }

        return this.$http
            .get(this.nsjRouting.generate('persona_trabalhadores_foto', params, true), {
                responseType: 'arraybuffer',
                timeout: this.fotosCancelDeferred.promise
            })
            .then((response: any) => {
                if (!response.data || response.data.byteLength === 0) {
                    return null;
                }

                const mimeType = response.headers('Content-Type');
                const file = new Blob([response.data], { type: mimeType });
                const fileUrl = URL.createObjectURL(file);
                this.objectUrls.push(fileUrl);

                return fileUrl;
            });
    }
}
